<template>
  <div class="app-story-carousel" :style="cssProps">
    <Swiper :options="swiperOptions" @ready="handleSwiperReady">
      <SwiperSlide v-for="slide in slides" :key="`${uniqueId}_story-slide_${slide.id}`" class="slide">
        <figure class="slide-content">
          <div class="slide-image-wrapper">
            <img class="slide-image" loading="lazy" :src="slide.media.url" :alt="slide.alt || slide.title" />
            <AppMediaCreditLine v-if="slide.media.creditLine" :value="slide.media.creditLine" />
          </div>
          <figcaption v-if="slide.title" class="slide-title">{{ slide.title }}</figcaption>
        </figure>
      </SwiperSlide>

      <div ref="pagination" slot="pagination" class="swiper-pagination carousel-pagination" />

      <button :id="navBtnIds.prev" ref="prevBtn" slot="button-prev" class="carousel-nav-btn carousel-nav-btn--prev">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M13.2 7.19999L8.39995 12L13.2 16.8"
            stroke="#363636"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <button :id="navBtnIds.next" ref="nextBtn" slot="button-next" class="carousel-nav-btn carousel-nav-btn--next">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M10.8 16.8L15.6 12L10.8 7.20001" stroke="#363636" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </Swiper>
  </div>
</template>

<script>
import handleResizeWithDebounce from '@/helpers/windowResizeHandlerWithDebounce';

const SLIDES_PAGINATION_ELEM_WIDTH = 70;

export default {
  name: 'AppStoryCarousel',
  components: {
    AppMediaCreditLine: () => import('@/components/AppMediaCreditLine.vue'),
  },
  props: {
    navBtnSize: {
      type: Number,
      default: 28,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    slides: {
      type: Array,
      required: true,
    },
    uniqueId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      swiper: null,
    };
  },

  computed: {
    cssProps() {
      return {
        '--slides-nav-btn-size': this.navBtnSize + 'px',
        '--slides-pagination-width': SLIDES_PAGINATION_ELEM_WIDTH + 'px',
      };
    },
    swiperOptions() {
      return {
        initialSlide: 0,
        speed: 1000,
        preloadImages: false,

        slidesPerView: 1.15,
        spaceBetween: 16,
        centeredSlides: false,

        breakpoints: {
          768: {
            slidesPerView: 1.12,
            spaceBetween: 20,
            centeredSlides: true,
          },
          820: {
            slidesPerView: 1.19,
            spaceBetween: 25,
            centeredSlides: true,
          },
          912: {
            slidesPerView: 1.32,
            spaceBetween: 25,
            centeredSlides: true,
          },
          1024: {
            slidesPerView: 1.35,
            spaceBetween: 25,
            centeredSlides: true,
          },
          1180: {
            slidesPerView: 1.54,
            spaceBetween: 25,
            centeredSlides: true,
          },
          1200: {
            slidesPerView: 1.57,
            spaceBetween: 25,
            centeredSlides: true,
          },
          1366: {
            slidesPerView: 1.78,
            spaceBetween: 25,
            centeredSlides: true,
          },
          1400: {
            slidesPerView: 1.82,
            spaceBetween: 25,
            centeredSlides: true,
          },
        },

        ...this.options,

        pagination: {
          el: '.carousel-pagination',
          type: 'custom',
          renderCustom(swiper, current, total) {
            const currentText = current < 10 ? '0' + current : current;
            const totalText = total < 10 ? '0' + total : total;

            return `<span class="carousel-pagination__active">${currentText}</span>
<span class="carousel-pagination__delimiter">/</span>
<span class="carousel-pagination__total">${totalText}</span>`;
          },
        },
        navigation: {
          prevEl: `#${this.navBtnIds.prev}`,
          nextEl: `#${this.navBtnIds.next}`,
        },
      };
    },
    navBtnIds() {
      return {
        prev: `${this.uniqueId}_btn-prev`,
        next: `${this.uniqueId}_btn-next`,
      };
    },
  },

  mounted() {
    this._debounceResize = handleResizeWithDebounce();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this._debounceResize = null;
    this.swiper = null;
  },

  methods: {
    handleResize() {
      this._debounceResize(this.setActiveSlideLeftPosition);
    },
    handleSwiperReady(swiper) {
      this.swiper = swiper;
      this.setActiveSlideLeftPosition();
    },
    setActiveSlideLeftPosition() {
      if (!this.swiper) {
        return;
      }

      const activeSlide = this.swiper.slides[this.swiper.activeIndex];
      const containerLeftPos = this.swiper.el.getBoundingClientRect().left;

      const activeSlideLeftPos = Math.floor(activeSlide.getBoundingClientRect().left - containerLeftPos);
      const prevNavBtnLeftPos = activeSlideLeftPos + SLIDES_PAGINATION_ELEM_WIDTH + this.navBtnSize;
      const nextNavBtnLeftPos = prevNavBtnLeftPos + this.navBtnSize + 4;

      if (this.$refs.pagination) {
        this.$refs.pagination.style.left = `${activeSlideLeftPos}px`;
      }
      if (this.$refs.prevBtn) {
        this.$refs.prevBtn.style.left = `${prevNavBtnLeftPos}px`;
      }
      if (this.$refs.nextBtn) {
        this.$refs.nextBtn.style.left = `${nextNavBtnLeftPos}px`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.app-story-carousel {
  position: relative;

  .swiper-container {
    max-width: 1400px;
    width: 100%;
    margin: auto;
    overflow: hidden;
    padding-bottom: 30px;
  }

  .slide-content {
    margin: 0 0 16px;
  }

  .slide-image-wrapper {
    position: relative;
    display: flex;
  }

  .slide-image {
    width: 100%;
    height: auto;
    max-height: 600px;
    object-fit: cover;
    aspect-ratio: 239 / 150;
  }

  .slide-title {
    margin-top: 8px;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    color: vars.$c-dark-gray;
  }

  .carousel-pagination {
    bottom: 0;
    justify-content: flex-start;
    width: var(--slides-pagination-width);
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 2px;
    line-height: var(--slides-nav-btn-size);
    color: vars.$c-dark-gray;

    display: flex;
    align-items: center;

    ::v-deep .carousel-pagination {
      &__delimiter {
        margin: 0 5px;
      }

      &__total {
        color: vars.$c-gray-a2;
      }
    }
  }

  .carousel-nav-btn {
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 10;
    width: var(--slides-nav-btn-size);
    height: var(--slides-nav-btn-size);
    padding: 0;
    background-color: transparent;
    border: none;
    transition: opacity 0.2s;

    svg {
      width: 100%;
      height: 100%;
    }

    &.swiper-button-disabled {
      cursor: auto;
      pointer-events: none;

      path {
        stroke: vars.$c-gray-da;
      }
    }
  }
}
</style>
